import { PayloadAction } from '@reduxjs/toolkit';
import { ConferenceStatus } from '@yandex-360/telemost-sdk';
import { Participant } from '@yandex-video-platform/goloom-sdk';
import { buffers } from 'redux-saga';
import {
    actionChannel,
    all,
    call,
    fork,
    put,
    race,
    SagaGenerator,
    select,
    spawn,
    take,
    takeEvery,
} from 'typed-redux-saga';

import { initConferenceSaga, startPollConference, updateConferenceConfig } from '..';
import { ErrorPageType } from '../../../../../shared/components/error-page/types';
import { SELF } from '../../../../../shared/constants';
import { resetConfigRoomId, setErrorPageAction } from '../../../../../shared/features/config';
import {
    getAutoJoin,
    getCreatedWithReload,
    getGuestName,
    getRoomAgentConfig,
    isMessengerIntegration,
    selectIsDebugToolsEnabled,
    selectIsMeetingRoomIntegration,
    selectIsReconnectFlowV2Enabled,
    selectIsTeleMessenger,
    selectIsTranslatorIntegration,
    selectShouldWaitAccept,
} from '../../../../../shared/features/config/selectors';
import {
    broadcastManager,
    checkIfShouldStopJoinAttempts,
    getIsSdkError,
    goloomSdk,
} from '../../../../../shared/features/goloom-sdk';
import {
    hideNotification,
    NOTIFICATIONS_MAP,
    notify,
} from '../../../../../shared/features/notifications/ps-notifications';
import { parentFrameCommunicator } from '../../../../../shared/features/parent-frame-communicator';
import { setUser } from '../../../../../shared/features/user';
import { ChannelHolder } from '../../../../../shared/helpers/channelHolder';
import { isInsideIframe } from '../../../../../shared/helpers/iframe';
import { ACTION, COUNTERS, FEATURE } from '../../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { isDesktopSharingAvailable } from '../../../../../shared/helpers/media-devices';
import parseCallUri from '../../../../../shared/helpers/parse-call-uri';
import { RetryWithTimeoutError } from '../../../../../shared/helpers/redux-saga/errors';
import { retryWithTimeout, RetryWithTimeoutParams } from '../../../../../shared/helpers/redux-saga/retry-with-timeout';
import { ConfigurableBackoffStrategy } from '../../../../../shared/helpers/retry-strategy/configurable-backoff';
import { ExponentialBackoffStrategy } from '../../../../../shared/helpers/retry-strategy/exponential-backoff';
import { cloudApi } from '../../../../../shared/helpers/transport';
import { ConnectionType } from '../../../../../types';
import { handleErrorSaga } from '../../../helpers/error';
import { handleFetchError, isNonCriticalFetchError } from '../../../helpers/handlers-sagas';
import { acceptCallAction } from '../../../iframe-sagas/slice';
import { OutgoingEvents } from '../../../iframe-sagas/types';
import {
    getChatId,
    getConferenceConfig,
    getJoinedAsGuest,
    isChatAllowedInConference,
    isLocalRecordingAllowed,
} from '../../../selectors';
import { resetCall } from '../../../slice';
import { ConferenceConfig, JoinConferencePayload, NotificationId } from '../../../types';
import { joinTheChatSaga } from '../../chat';
import { localVadChangedSaga } from '../../device/sagas/local-vad';
import {
    setUserPreferredGridViewAction,
    setUserPreferredGridViewSaga,
    slotsChangedSaga,
} from '../../goloom-video-grid';
import { watchOfflineSaga, watchOnlineSaga, watchScheduleNetworkChangeSaga } from '../../offline';
import { updatePermissionsInfoSaga } from '../../permissions/update-permissions-saga';
import { recordConferenceAction } from '../../record/slice';
import { initRoomAgentSaga } from '../../room-agent';
import { setIsDesktopSharingEnabled } from '../../screen-sharing/slice';
import { shareCallAction } from '../../share-link/slice';
import { waitingRoomsSaga } from '../../waiting-rooms';
import {
    pollingWaitingRoomFailedAction,
    pollingWaitingRoomSuccessAction,
    startPollWaitingRoomAccessAction,
} from '../../waiting-rooms/slice';
import {
    cancelCallAction,
    connectedToConferenceAction,
    connectedToConferenceDoneAction,
    setCloseRoomInitiatedBySelf,
    setConferenceStateVersion,
    setConferenceStatus,
    setJoinedAsGuest,
    setMyUserId,
    setRoomConferenceConfig,
    startAsGuestConferenceAction,
    updateParticipant,
} from '../slice';
import { changeUserSaga } from './me';
import { onNotificationSaga } from './notification';
import {
    participantsAddedSaga,
    participantsRemovedSaga,
    participantsUpdatedSaga,
    updatePeersInfoSaga,
} from './participants';
import { reconnectWatchSaga } from './reconnect';

const logger = createLogger('goloom:join');

const DEFAULT_JOIN_TIMEOUT = 40000;
const JOIN_MAX_ATTEMPTS = 3;

// Запоминаем принят ли звонок, чтобы не висеть в ожидании принятия
// после перезагрузки iframe, после нажатия на "Попробовать снова".
const ACCEPTED_CALL_STORAGE_KEY_PREFIX = 'telemost:accepted-call:';
const getAcceptedCallStorageKey = (roomId: string) => `${ACCEPTED_CALL_STORAGE_KEY_PREFIX}${roomId}`;

function* getIsCallAccepted(roomId: string) {
    const isCallAccepted = yield* call([sessionStorage, sessionStorage.getItem], getAcceptedCallStorageKey(roomId));
    return isCallAccepted === 'true';
}

function* rememberCallAccepted(roomId: string) {
    yield* call([sessionStorage, sessionStorage.setItem], getAcceptedCallStorageKey(roomId), 'true');
}

function* updateConferenceConfigSaga(
    roomId: string,
    searchString?: string,
    finalDisplayName?: string,
    fallbackConfig?: ConferenceConfig
): SagaGenerator<ConferenceConfig | undefined> {
    try {
        return yield* call(updateConferenceConfig, roomId, searchString, finalDisplayName);
    } catch (error) {
        logger.error(error, {
            level: isNonCriticalFetchError(error) ? 'warning' : 'error',
            method: 'updateConferenceConfigSaga',
        });

        if (yield* call(handleFetchError, error)) {
            return fallbackConfig;
        }

        yield* put(
            setErrorPageAction({
                type: ErrorPageType.DEFAULT,
                reason: {
                    method: 'updateConferenceConfigSaga',
                    message: error instanceof Error ? error.message : 'Unexpected error updating conference config',
                    name: error instanceof Error ? error.name : undefined,
                },
            })
        );

        return fallbackConfig;
    }
}

export function* join(config: ConferenceConfig, roomId: string, searchString?: string, displayName?: string) {
    const timeout = config.client_configuration?.goloom_session_open_ms ?? DEFAULT_JOIN_TIMEOUT;
    const isReconnectFlowV2Enabled = yield* select(selectIsReconnectFlowV2Enabled);

    try {
        return yield* call(
            [goloomSdk, retryWithTimeout],
            {
                maxTries: JOIN_MAX_ATTEMPTS,
                timeout,
                getArgs: function* (attempt) {
                    if (attempt === 0) {
                        return [
                            {
                                userId: config.peer_id,
                                credentials: config.credentials,
                                name: config.display_name || displayName || '',
                                roomId: config.room_id,
                                server: config.client_configuration?.media_server_url,
                                iceServers: config.client_configuration?.ice_servers,
                                serviceName: config.client_configuration?.service_name,
                            },
                        ];
                    } else {
                        // Инициализируем девайсы до запроса конфига
                        yield* call(initConferenceSaga, { reinit: true, syncInitDevices: true });
                    }

                    // Перезапрашиваем config, так как нам нужны новые credentials
                    const currentConfig = yield* call(updateConferenceConfig, roomId, searchString, displayName);

                    return [
                        {
                            userId: currentConfig.peer_id,
                            credentials: currentConfig.credentials,
                            name: currentConfig.display_name || displayName || '',
                            roomId: currentConfig.room_id,
                            server: currentConfig.client_configuration?.media_server_url,
                            iceServers: currentConfig.client_configuration?.ice_servers,
                            serviceName: currentConfig.client_configuration?.service_name,
                        },
                    ];
                },
                beforeRun: function (attempt: number) {
                    logger.startDelta(COUNTERS.CONNECT_GOLOOM_DELTA);
                    logger.featureEvent({
                        feature: FEATURE.CONNECTION,
                        action: ACTION.CONNECT_GOLOOM,
                        options: {
                            additional: {
                                roomId,
                                isRetry: attempt > 0,
                                peerId: config.peer_id,
                            },
                        },
                    });
                },
                onSuccess: () => {
                    logger.endDelta(COUNTERS.CONNECT_GOLOOM_DELTA, {
                        status: 'success',
                    });
                    logger.featureEvent({
                        feature: FEATURE.CONNECTION,
                        action: ACTION.CONNECT_GOLOOM,
                        isSuccess: true,
                    });
                },
                onAttemptError: (error, attempt) => {
                    const retryStrategyType = isReconnectFlowV2Enabled ? 'configurable' : 'exponential';
                    logger.warn(error, {
                        method: 'joinWithTimeoutAndRetry',
                        additional: {
                            kind: 'unable_connect_goloom',
                            timeout: timeout / 1000,
                            retryN: attempt,
                            retryStrategy: retryStrategyType,
                            reason: error instanceof Error ? error.message : undefined,
                        },
                    });
                    logger.endDelta(COUNTERS.CONNECT_GOLOOM_DELTA, {
                        status: 'failed',
                    });
                    logger.featureEvent({
                        feature: FEATURE.CONNECTION,
                        action: ACTION.CONNECT_GOLOOM,
                        isSuccess: false,
                        options: {
                            additional: {
                                roomId,
                                isRetry: attempt > 0,
                                peerId: config.peer_id,
                                retryStrategy: retryStrategyType,
                                reason: error instanceof Error ? error.message : undefined,
                            },
                        },
                    });
                    return checkIfShouldStopJoinAttempts(error, isReconnectFlowV2Enabled);
                },
                retryStrategy: isReconnectFlowV2Enabled
                    ? new ConfigurableBackoffStrategy()
                    : new ExponentialBackoffStrategy(),
            } as RetryWithTimeoutParams<typeof goloomSdk.join>,
            goloomSdk.join,
            {
                userId: config.peer_id,
                credentials: config.credentials,
                name: config.display_name || displayName || '',
                roomId: config.room_id,
                server: config.client_configuration?.media_server_url,
                iceServers: config.client_configuration?.ice_servers,
                serviceName: config.client_configuration?.service_name,
            }
        ) as SagaGenerator<Participant[] | undefined>;
    } catch (error) {
        if (error instanceof RetryWithTimeoutError) {
            throw RetryWithTimeoutError.enhance(error, {
                additional: {
                    kind: 'unable_connect_goloom',
                    timeout: timeout / 1000,
                    // попытки считаем с 0, потому вычитаем
                    retryN: JOIN_MAX_ATTEMPTS - 1,
                    reason: error.message,
                },
            });
        }

        let isRedirected = false;
        const isSdkError = getIsSdkError(error);
        if (isSdkError) {
            isRedirected = yield* call(handleErrorSaga, { error });
        }

        if (!isRedirected) {
            yield* put(
                setErrorPageAction({
                    type: ErrorPageType.DEFAULT,
                    reason: { method: 'join', message: 'Connect to room failed' },
                })
            );
        }

        throw error;
    }
}

function* startPollWaitingRoomAccessSaga(
    roomId: string,
    config?: ConferenceConfig,
    searchString?: string,
    finalDisplayName?: string
): SagaGenerator<ConferenceConfig | undefined> {
    if (!config) {
        return;
    }

    logger.event('Waiting room polling started', {
        additional: {
            method: 'startPollWaitingRoomAccessSaga',
        },
    });

    yield* put(setConferenceStatus(ConferenceStatus.WAITING_ROOM));
    yield* put(startPollWaitingRoomAccessAction({ roomId, config }));

    const { pollingSuccess } = yield* race({
        pollingSuccess: take(pollingWaitingRoomSuccessAction),
        pollingFailed: take(pollingWaitingRoomFailedAction),
        cancelCall: take(cancelCallAction),
    });

    if (pollingSuccess) {
        logger.event('Waiting room polling succeeded', {
            additional: {
                method: 'startPollWaitingRoomAccessSaga',
            },
        });
        return yield* call(updateConferenceConfigSaga, roomId, searchString, finalDisplayName);
    } else {
        logger.event('Waiting room polling failed', {
            additional: {
                method: 'startPollWaitingRoomAccessSaga',
            },
        });
        return;
    }
}

/**
 * Сага присоединения к конференции
 */
export function* joinConferenceSaga({ payload }: PayloadAction<JoinConferencePayload>) {
    const channelHolder = new ChannelHolder();
    const { roomId, searchString } = payload;
    let waiterMetricStarted = false;

    yield* put(setCloseRoomInitiatedBySelf(false));
    yield* put(setConferenceStateVersion(-1));
    logger.event('Joining started', {
        additional: {
            method: 'joinConferenceSaga',
        },
    });

    try {
        let config = yield* select(getConferenceConfig);

        if (config && parseCallUri(config.uri).roomId !== roomId) {
            yield* put(resetCall());
            yield put(resetConfigRoomId());
        }

        const isDebugToolsEnabled = yield* select(selectIsDebugToolsEnabled);
        if (isDebugToolsEnabled) {
            // @ts-expect-error
            window.__goloomSdk = goloomSdk;
        }

        /**
         * У нас отсутствует конфиг когда мы подключаемся к конференции
         */
        const isJoiningAsGuest = !config;

        const autoJoinEnabled = yield* select(getAutoJoin);
        const isMeetingRoomIntegration = yield* select(selectIsMeetingRoomIntegration);
        const autoJoin = autoJoinEnabled || isMeetingRoomIntegration;

        yield* call([cloudApi, cloudApi.startCaptureConferenceCluster]);

        if (isJoiningAsGuest) {
            yield* put(setJoinedAsGuest(true));
        } else {
            yield* call(initConferenceSaga, {
                syncInitDevices: true,
            });
        }

        yield put(setIsDesktopSharingEnabled(isDesktopSharingAvailable()));
        yield put(setConferenceStatus(ConferenceStatus.READY_TO_JOIN));

        const shouldWaitAccept = yield* select(selectShouldWaitAccept);
        const shouldSkipAcceptCall = yield* call(getIsCallAccepted, roomId);
        if (shouldWaitAccept && isInsideIframe() && autoJoin && !shouldSkipAcceptCall) {
            yield* take(acceptCallAction);
            yield* call(rememberCallAccepted, roomId);
        }

        const joinedAsGuest = yield* select(getJoinedAsGuest);
        const guestName = yield* select(getGuestName);

        let finalDisplayName: string | undefined;

        if (joinedAsGuest) {
            let displayName;

            // Создаем канал с буфером для экшена startAsGuestConferenceAction,
            // так как он может быть вызван до того, как мы подписываемся на него.
            // Задержка может возникнуть в initConferenceSaga из-за сетевых запросов
            const startAsGuestConferenceActionChannel = yield* actionChannel(
                startAsGuestConferenceAction,
                buffers.sliding(1)
            );

            yield* call(initConferenceSaga, {
                syncInitDevices: true,
            });

            if (autoJoin) {
                displayName = guestName;
            } else {
                const { payload } = yield* take(startAsGuestConferenceActionChannel);
                displayName = payload?.displayName;
            }

            finalDisplayName = displayName;
        }

        yield* put(setConferenceStatus(ConferenceStatus.JOINING));

        const shouldShowNotification = autoJoin;
        if (shouldShowNotification) {
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.JOINING]()));
        }

        // Всегда перезапрашиваем конфиг перед подключением, чтобы получить свежие credentials,
        // поскольку они могли протухнуть за время ожидания разрешений на доступ к устройствам.
        config = yield* call(updateConferenceConfigSaga, roomId, searchString, finalDisplayName, config);

        if (config?.peers) {
            yield* call(updatePeersInfoSaga, config.peers, { selfPeerId: config.peer_id });
        }

        if (config?.permissions) {
            yield* call(updatePermissionsInfoSaga, config.permissions);
        }

        logger.event('Conference config received', {
            additional: {
                method: 'joinConferenceSaga',
            },
        });
        const isWaitingRoomGuest = config?.connection_type === ConnectionType.WAITING_ROOM;

        if (config?.waiting_room_available || isWaitingRoomGuest) {
            yield* fork(waitingRoomsSaga);
        }

        if (isWaitingRoomGuest && shouldShowNotification) {
            yield* put(hideNotification(NotificationId.JOINING));
        }

        if (isWaitingRoomGuest) {
            config = yield* call(startPollWaitingRoomAccessSaga, roomId, config, searchString, finalDisplayName);

            logger.startFeatureDelta({
                feature: FEATURE.WAITING_ROOM,
                action: ACTION.WAITER,
                id: roomId,
            });
            waiterMetricStarted = true;
        }

        if (!config) {
            return;
        }

        if (config.peer_id) {
            yield* put(setMyUserId(config.peer_id));
        }

        if (joinedAsGuest && finalDisplayName && config) {
            yield* put(
                setRoomConferenceConfig({
                    ...config,
                    display_name: finalDisplayName,
                })
            );
        }

        if (!finalDisplayName) {
            finalDisplayName = config?.display_name || '';
        }

        yield call([broadcastManager, broadcastManager.init], {
            uri: config.uri,
            broadcastUri: config.conference_state?.broadcast_data?.broadcast_uri,
            conferenceConfigOptions: {
                roomId,
                searchString,
                displayName: finalDisplayName,
            },
        });

        const urlParams = new URLSearchParams(location.search);
        const shouldShare = urlParams.get('share');

        const createdWithReload = yield* select(getCreatedWithReload);

        // После создания конфы у организатора надо скопировать ссылку в буфер и показать нотифайку
        // Параметр `share` может появиться если после создания организатор нажал "Открыть в приложении"
        // Если при создании конференции была автоматическая перезагрузка страницы, то нужно скопировать ссылку в буфер
        if (!joinedAsGuest || shouldShare || createdWithReload) {
            yield put(shareCallAction({ calledOnJoin: true }));
        }

        yield* call([goloomSdk, goloomSdk.setName], finalDisplayName || '');
        yield* put(
            updateParticipant({
                id: SELF,
                displayName: finalDisplayName,
            })
        );

        const roomAgentConfig = yield* select(getRoomAgentConfig);

        if (roomAgentConfig) {
            yield* fork(initRoomAgentSaga);
        }

        const participants = yield* call(join, config, roomId, searchString, finalDisplayName);
        logger.event('Connect to room succeeded', {
            additional: {
                method: 'joinConferenceSaga',
            },
        });

        yield put(connectedToConferenceAction());

        if (participants) {
            const meParticipant = goloomSdk.getMe();

            if (meParticipant) {
                participants.push(meParticipant);
            }

            // spawn (не call): даём базовую заливку участников отработать синхронно, а cloud-запрос
            // в participantsAddedSaga пусть тикает в фоне — иначе залипаем на «Подключение…»
            // пока ждём peers/state.
            yield* spawn(participantsAddedSaga, participants);
            yield call(slotsChangedSaga);
        }

        yield put(setConferenceStatus(ConferenceStatus.JOINED));

        const isMessenger = yield* select(isMessengerIntegration);
        const isTeleMessenger = yield* select(selectIsTeleMessenger);
        if (!autoJoin && (isMessenger || isTeleMessenger)) {
            yield* call(parentFrameCommunicator.sendMessage, 'messenger', { event: OutgoingEvents.START_CALL_CLICK });
        }

        if (shouldShowNotification) {
            yield* put(hideNotification(NotificationId.JOINING));
        }

        const isChatAllowed = yield* select(isChatAllowedInConference);

        if (isChatAllowed) {
            const chatId = yield* select(getChatId);
            const isTranslatorIntegration = yield* select(selectIsTranslatorIntegration);

            if (chatId && !isTranslatorIntegration) {
                yield* spawn(joinTheChatSaga);
            }
        }

        const isRecordingAvailable = yield* select(isLocalRecordingAllowed);

        if (isRecordingAvailable && urlParams.get('record') === 'on') {
            yield* put(recordConferenceAction());
        }

        yield* put(connectedToConferenceDoneAction());

        const effects = [
            channelHolder.takeEvery(goloomSdk.onParticipantsAdded, participantsAddedSaga),
            channelHolder.takeEvery(goloomSdk.onParticipantsRemoved, participantsRemovedSaga),
            channelHolder.takeEvery(goloomSdk.onParticipantsUpdated, participantsUpdatedSaga),
            channelHolder.takeEvery(goloomSdk.onSlotsChanged, slotsChangedSaga),
            channelHolder.takeEvery(goloomSdk.onLocalVad, localVadChangedSaga),
            channelHolder.takeEvery(goloomSdk.onNotification, onNotificationSaga),
            takeEvery(setUser, changeUserSaga),
            takeEvery(setUserPreferredGridViewAction, setUserPreferredGridViewSaga),
            call(startPollConference),
            call(watchOfflineSaga),
            call(watchOnlineSaga),
            call(watchScheduleNetworkChangeSaga),
            call(reconnectWatchSaga, {
                roomId,
                searchString,
                displayName: finalDisplayName,
            }),
        ];

        logger.event('Joining succeeded', {
            additional: {
                method: 'joinConferenceSaga',
            },
        });

        if (joinedAsGuest) {
            logger.endFeatureDelta({
                feature: FEATURE.CONNECTION,
                action: ACTION.JOIN_CONFERENCE,
                isSuccess: true,
                options: {
                    additional: {
                        roomId,
                        userId: config.peer_id,
                    },
                },
            });
        }

        if (waiterMetricStarted) {
            logger.endFeatureDelta({
                feature: FEATURE.WAITING_ROOM,
                action: ACTION.WAITER,
                id: roomId,
                isSuccess: true,
            });
            waiterMetricStarted = false;
        }

        yield* all(effects);
    } catch (error) {
        logger.error(error, {
            level: 'error',
            method: 'joinConferenceSaga',
        });

        const joinedAsGuest = yield* select(getJoinedAsGuest);
        if (joinedAsGuest) {
            const config = yield* select(getConferenceConfig);
            logger.endFeatureDelta({
                feature: FEATURE.CONNECTION,
                action: ACTION.JOIN_CONFERENCE,
                isSuccess: false,
                options: {
                    additional: {
                        roomId,
                        userId: config?.peer_id,
                    },
                },
            });
        }

        if (waiterMetricStarted) {
            logger.endFeatureDelta({
                feature: FEATURE.WAITING_ROOM,
                action: ACTION.WAITER,
                isSuccess: false,
                id: roomId,
            });
            waiterMetricStarted = false;
        }
    } finally {
        if (waiterMetricStarted) {
            logger.discardFeatureDelta({
                feature: FEATURE.WAITING_ROOM,
                action: ACTION.WAITER,
                id: roomId,
            });
        }

        channelHolder.close();
    }
}
